import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/auth'
import { supabase, Vehicle, TowerService } from '../lib/supabase'
import { logActivity } from '../lib/activityLogger'
import {
  ArrowLeft,
  MapPin,
  Truck,
  Navigation,
  Clock,
  Star,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Shield,
  X,
  Users,
  Battery,
  Fuel,
  Search,
  Wrench
} from 'lucide-react'
import { getNearbyTowersWithRates, customerSelectTower, TowerWithRate } from '../services/automatedTowerService'

export default function TowingRequestPage() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [selectedVehicle, setSelectedVehicle] = useState<string>('')
  const [showNewVehicleForm, setShowNewVehicleForm] = useState(false)
  const [newVehicle, setNewVehicle] = useState({
    plate: '',
    make: '',
    model: '',
    year: '',
    color: ''
  })
  const [destination, setDestination] = useState('')
  const [gpsLat, setGpsLat] = useState<number | null>(null)
  const [gpsLng, setGpsLng] = useState<number | null>(null)
  const [gpsStatus, setGpsStatus] = useState<'idle' | 'loading' | 'success' | 'denied'>('idle')
  const [autoLocationLabel, setAutoLocationLabel] = useState('')
  const [nearbyTowers, setNearbyTowers] = useState<TowerWithRate[]>([])
  const [selectedTower, setSelectedTower] = useState<string>('')
  const [loadingTowers, setLoadingTowers] = useState(false)
  const [sortBy, setSortBy] = useState<'price' | 'distance' | 'eta'>('price')
  const [destinationAddress, setDestinationAddress] = useState('')
  const [destinationLat, setDestinationLat] = useState<number | null>(null)
  const [destinationLng, setDestinationLng] = useState<number | null>(null)
  const [destinationSearch, setDestinationSearch] = useState('')
  const [destinationSuggestions, setDestinationSuggestions] = useState<any[]>([])
  const [loadingDestinations, setLoadingDestinations] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [serviceType, setServiceType] = useState<'towing' | 'battery' | 'flat_tyre' | 'fuel'>('towing')
  const [selectedTowerService, setSelectedTowerService] = useState<TowerService | null>(null)
  const [towerServices, setTowerServices] = useState<TowerService[]>([])
  const [loadingServices, setLoadingServices] = useState(false)
  const [allTowingServices, setAllTowingServices] = useState<TowerService[]>([])
  const [loadingAllServices, setLoadingAllServices] = useState(false)
  const [serviceSearch, setServiceSearch] = useState('')
  const [step, setStep] = useState<'service' | 'location' | 'vehicle' | 'service_types' | 'towers' | 'confirm'>('service')
  const [fuelQuantity, setFuelQuantity] = useState<number>(10)
  const [fuelType, setFuelType] = useState<'petrol' | 'diesel' | 'kerosene'>('petrol')
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [gpsError, setGpsError] = useState<string>('')
  const [permissionState, setPermissionState] = useState<'granted' | 'denied' | 'prompt' | 'unknown'>('unknown')

  useEffect(() => {
    if (!profile) return
    supabase.from('vehicles').select('*').eq('owner_id', profile.id).then(({ data }) => setVehicles((data as Vehicle[]) || []))

    // Load all active towing services for service type selection
    loadAllTowingServices()

    // Check location permission state on mount
    checkLocationPermission().then(state => {
      setPermissionState(state)
      if (state === 'denied') {
        setGpsError('Location access is blocked. Please enable location permissions in your browser settings.')
        setGpsStatus('denied')
      }
    })

    // Check for preselected service from navigation state
    if (location.state?.preselectedService) {
      const preselected = location.state.preselectedService as 'towing' | 'battery' | 'flat_tyre' | 'fuel'
      setServiceType(preselected)
      setStep('location')
    }
  }, [profile, location.state])

  const loadAllTowingServices = async () => {
    setLoadingAllServices(true)
    try {
      const { data, error } = await supabase
        .from('tower_services')
        .select('*, profiles!inner(business_name, full_name)')
        .eq('is_active', true)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error loading all towing services:', error)
        setAllTowingServices([])
        return
      }

      console.log('Loaded towing services:', data)
      setAllTowingServices((data as any[]) || [])
    } catch (error) {
      console.error('Error loading all towing services:', error)
      setAllTowingServices([])
    } finally {
      setLoadingAllServices(false)
    }
  }

  const loadMatchingServices = async (serviceType: 'fuel' | 'battery' | 'flat_tyre') => {
    setLoadingAllServices(true)
    try {
      // Search for services matching the service type in service_name or description
      const searchTerms = serviceType === 'battery' ? ['battery', 'jump', 'start'] : (serviceType === 'flat_tyre' ? ['tyre', 'tire', 'flat', 'change'] : ['fuel', 'delivery', 'petrol', 'diesel'])
      
      const { data, error } = await supabase
        .from('tower_services')
        .select('*, profiles!inner(business_name, full_name)')
        .eq('is_active', true)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error loading matching services:', error)
        setAllTowingServices([])
        return
      }

      // Filter services that match the service type
      const matchingServices = (data as any[]).filter(service => {
        const serviceName = service.service_name?.toLowerCase() || ''
        const description = service.description?.toLowerCase() || ''
        return searchTerms.some(term => serviceName.includes(term) || description.includes(term))
      })

      console.log(`Loaded matching ${serviceType} services:`, matchingServices)
      setAllTowingServices(matchingServices)
    } catch (error) {
      console.error('Error loading matching services:', error)
      setAllTowingServices([])
    } finally {
      setLoadingAllServices(false)
    }
  }

  // Load tower services when a tower is selected
  useEffect(() => {
    if (!selectedTower) {
      setTowerServices([])
      setSelectedTowerService(null)
      return
    }

    loadTowerServices(selectedTower)
  }, [selectedTower])

  // Filter towers when a service is selected
  useEffect(() => {
    if (selectedTowerService && nearbyTowers.length > 0) {
      const towersWithService = nearbyTowers.filter(tower =>
        allTowingServices.some(s => s.tower_id === tower.id && s.service_name === selectedTowerService.service_name)
      )
      setNearbyTowers(towersWithService)
    }
  }, [selectedTowerService])

  const loadTowerServices = async (towerId: string) => {
    setLoadingServices(true)
    try {
      const { data, error } = await supabase
        .from('tower_services')
        .select('*')
        .eq('tower_id', towerId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error loading tower services:', error)
        setTowerServices([])
        return
      }

      setTowerServices((data as TowerService[]) || [])
    } catch (error) {
      console.error('Error loading tower services:', error)
      setTowerServices([])
    } finally {
      setLoadingServices(false)
    }
  }

  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371 // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLng = (lng2 - lng1) * Math.PI / 180
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
    return R * c
  }

  // Check geolocation permission state
  const checkLocationPermission = async (): Promise<'granted' | 'denied' | 'prompt' | 'unknown'> => {
    if (!('geolocation' in navigator)) {
      return 'unknown'
    }

    // Try to use the Permissions API if available (Chrome, Edge, Firefox)
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
        return result.state as 'granted' | 'denied' | 'prompt'
      } catch (e) {
        console.log('Permissions API not available for geolocation')
      }
    }

    return 'unknown'
  }

  const detectLocation = async () => {
    if (!('geolocation' in navigator)) {
      setGpsError('Geolocation is not supported by your browser. Please use a modern browser like Chrome, Firefox, or Edge.')
      setGpsStatus('denied')
      setPermissionState('unknown')
      return
    }

    // Check permission state first
    const permission = await checkLocationPermission()
    setPermissionState(permission)

    if (permission === 'denied') {
      setGpsError('Location access is blocked. Please enable location permissions in your browser settings, then refresh the page.')
      setGpsStatus('denied')
      return
    }

    setGpsStatus('loading')
    setGpsError('')

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = Number(pos.coords.latitude.toFixed(7))
        const lng = Number(pos.coords.longitude.toFixed(7))

        setGpsLat(lat)
        setGpsLng(lng)
        setGpsStatus('success')
        setPermissionState('granted')
        setGpsError('')

        // Get place name
        const placeName = await getPlaceName(lat, lng)
        setAutoLocationLabel(placeName)

        // Load nearby towers for towing and fuel delivery
        if (serviceType === 'towing') {
          loadNearbyTowers(lat, lng, 'towing')
        }
      },
      (error) => {
        console.error('GPS Error:', error)

        let errorMessage = 'Unable to get your location'
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access in your browser settings. Click the lock icon in the address bar to change permissions.'
            setPermissionState('denied')
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable. Please check your device settings and ensure GPS is enabled.'
            break
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again or enter location manually.'
            break
          default:
            errorMessage = 'Unknown error getting location. Please enter location manually.'
        }

        setGpsError(errorMessage)
        setGpsStatus('denied')
      },
      { enableHighAccuracy: true, timeout: 25000, maximumAge: 0 }
    )
  }

  const getPlaceName = async (lat: number, lng: number): Promise<string> => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16`)
      const data = await res.json()
      return data?.display_name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`
    } catch {
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`
    }
  }

  const loadNearbyTowers = async (lat: number, lng: number, service: 'towing' | 'fuel' = 'towing') => {
    setLoadingTowers(true)
    // Use larger search radius for battery, flat tyre, and fuel services (100km vs 30km for towing)
    const searchRadius = (serviceType === 'battery' || serviceType === 'flat_tyre' || serviceType === 'fuel') ? 100 : 30
    const towers = await getNearbyTowersWithRates(lat, lng, service, searchRadius)
    setNearbyTowers(towers)
    setLoadingTowers(false)
  }

  const getPriceTier = (cost: number, allCosts: number[]): { label: string; color: string } => {
    if (allCosts.length === 0) return { label: 'Standard', color: '#3b82f6' }
    const avg = allCosts.reduce((sum, c) => sum + c, 0) / allCosts.length
    if (cost <= avg * 0.8) return { label: 'Budget', color: '#10b981' }
    if (cost >= avg * 1.2) return { label: 'Premium', color: '#8b5cf6' }
    return { label: 'Standard', color: '#3b82f6' }
  }

  const getSortedTowers = () => {
    const sorted = [...nearbyTowers]
    switch (sortBy) {
      case 'price':
        // Sort by calculated cost using selected service rates
        return sorted.sort((a, b) => {
          const costA = (selectedTowerService?.base_price || a.base_towing_rate || 1500) + (a.distance_km * (selectedTowerService?.per_km_rate || a.per_km_rate || 100))
          const costB = (selectedTowerService?.base_price || b.base_towing_rate || 1500) + (b.distance_km * (selectedTowerService?.per_km_rate || b.per_km_rate || 100))
          return costA - costB
        })
      case 'distance':
        return sorted.sort((a, b) => a.distance_km - b.distance_km)
      case 'eta':
        return sorted.sort((a, b) => a.eta_minutes - b.eta_minutes)
      default:
        return sorted
    }
  }

  const sortedTowers = getSortedTowers()
  
  // Filter towers to only show the tower who created the selected service
  const towersToShow = selectedTowerService 
    ? sortedTowers.filter(t => {
      console.log('Tower check:', t.id, 'vs tower_id:', selectedTowerService.tower_id, 'match:', t.id === selectedTowerService.tower_id)
      return t.id === selectedTowerService.tower_id
    })
    : sortedTowers
  
  const allCosts = towersToShow.map(t => (selectedTowerService?.base_price || t.base_towing_rate || 1500) + (t.distance_km * (selectedTowerService?.per_km_rate || t.per_km_rate || 100)))

  const searchDestinations = async (query: string) => {
    if (!query || query.length < 3) {
      setDestinationSuggestions([])
      return
    }

    setLoadingDestinations(true)
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&countrycodes=ke&addressdetails=1`
      )
      const data = await res.json()
      setDestinationSuggestions(data || [])
    } catch (error) {
      console.error('Error searching destinations:', error)
      setDestinationSuggestions([])
    } finally {
      setLoadingDestinations(false)
    }
  }

  const getPreviousStep = () => {
    if (step === 'confirm') {
      if (serviceType === 'towing') return 'towers'
      if (serviceType === 'fuel') return 'towers'
      if (serviceType === 'battery' || serviceType === 'flat_tyre') return 'towers'
      return 'vehicle'
    }
    if (step === 'towers') {
      if (serviceType === 'towing') return 'service_types'
      if (serviceType === 'fuel' || serviceType === 'battery' || serviceType === 'flat_tyre') return 'service_types'
      return 'location'
    }
    if (step === 'service_types') return 'location'
    if (step === 'vehicle') return 'location'
    if (step === 'location') return 'service'
    return 'service'
  }

  const calculateServicePrice = (service: TowerService, distanceKm: number = 0): number => {
    let price = service.base_price
    if (service.per_km_rate > 0 && distanceKm > 0) {
      price += service.per_km_rate * distanceKm
    }
    return price
  }

  const handleBack = () => {
    setStep(getPreviousStep() as any)
  }

  const handleBookTower = async () => {
    // Show confirmation modal instead of direct submission
    setShowConfirmModal(true)
  }

  const confirmSubmit = async () => {
    setShowConfirmModal(false)
    setProcessing(true)
    setError('')

    const reference = `MK${Date.now().toString().slice(-6)}`

    try {
      let estimatedCost = 0
      let selectedTowerId = null
      let serviceId = null
      const tower = nearbyTowers.find(t => t.id === selectedTower)

      if (serviceType === 'towing' || serviceType === 'fuel' || serviceType === 'battery' || serviceType === 'flat_tyre') {
        estimatedCost = tower?.estimated_cost || 0
        selectedTowerId = selectedTower

        // Use provider-defined service if selected
        if (selectedTowerService) {
          serviceId = selectedTowerService.id
          // Calculate price based on tower service
          const distance = tower?.distance_km || 0
          estimatedCost = calculateServicePrice(selectedTowerService, distance)
        } else {
          // Calculate cost based on base rate + distance
          const baseRate = serviceType === 'towing' ? (tower?.base_towing_rate || 1500) : (serviceType === 'fuel' ? (tower?.fuel_delivery_base_rate || 500) : (serviceType === 'battery' ? 1500 : (serviceType === 'flat_tyre' ? 800 : 500)))
          const perKmRate = serviceType === 'towing' ? (tower?.per_km_rate || 100) : (serviceType === 'fuel' ? (tower?.fuel_delivery_per_km_rate || 50) : 50)
          const distance = tower?.distance_km || 0
          estimatedCost = baseRate + (distance * perKmRate)
        }
      } else {
        // Estimate costs for other services
        if (serviceType === 'battery') estimatedCost = 1500
        if (serviceType === 'flat_tyre') estimatedCost = 800
      }

      // Build issue description with service details
      let issueDescription = `${serviceType.replace('_', ' ')} service request`
      if (selectedTowerService) {
        issueDescription = `${selectedTowerService.service_name} service request`
        if (selectedTowerService.description) {
          issueDescription += ` - ${selectedTowerService.description}`
        }
      }
      if (selectedVehicle || showNewVehicleForm) {
        issueDescription += ` - ${showNewVehicleForm ? newVehicle.plate : vehicles.find(v => v.id === selectedVehicle)?.plate || 'Vehicle'} (${showNewVehicleForm ? newVehicle.make : vehicles.find(v => v.id === selectedVehicle)?.make || 'Unknown'} ${showNewVehicleForm ? newVehicle.model : vehicles.find(v => v.id === selectedVehicle)?.model || ''})`
      }

      if (serviceType === 'fuel') {
        issueDescription += ` - ${fuelQuantity}L ${fuelType} (Tower will get fuel from nearest station)`
      }

      const { error: jobError } = await supabase.from('tow_jobs').insert({
        customer_id: profile?.id,
        reference,
        vehicle_id: selectedVehicle || null,
        service_type: serviceType,
        service_id: serviceId,
        status: 'pending',
        pickup_address: autoLocationLabel || destination,
        pickup_lat: gpsLat,
        pickup_lng: gpsLng,
        destination_address: (serviceType === 'towing' && selectedTowerService?.requires_destination) ? (destinationAddress || 'Customer location') : null,
        destination_lat: (serviceType === 'towing' && selectedTowerService?.requires_destination) ? destinationLat : null,
        destination_lng: (serviceType === 'towing' && selectedTowerService?.requires_destination) ? destinationLng : null,
        issue_description: issueDescription,
        payment_method: 'mpesa',
        total_amount: estimatedCost,
        operator_id: selectedTowerId,
        selected_tower_id: selectedTowerId,
        operator_distance_km: (serviceType === 'towing' || serviceType === 'fuel') ? (tower?.distance_km || 0) : 0,
        fuel_quantity: serviceType === 'fuel' ? fuelQuantity : null,
        fuel_type: serviceType === 'fuel' ? fuelType : null,
      })

      if (jobError) {
        setError('Failed to create request: ' + jobError.message)
        setProcessing(false)
        return
      }

      // Log activity
      logActivity('tow_request_created', '/towing-request', {
        reference,
        service_type: serviceType,
        amount: estimatedCost
      })

      setSuccess(true)
      setTimeout(() => {
        navigate('/customer')
      }, 2000)
    } catch (err) {
      setError('Failed to process request. Please try again.')
      setProcessing(false)
    }
  }

  if (!profile) return null

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        padding: '1rem'
      }}>
        <div className="login-window">
          <div style={{ textAlign: 'center' }}>
            <CheckCircle2 size={64} style={{ color: 'var(--green)', margin: '0 auto 1rem' }} />
            <h2 style={{ color: 'var(--text)', marginBottom: '0.5rem' }}>Request Submitted!</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>
              Your towing request has been submitted. The selected tower will contact you shortly.
            </p>
            <div style={{
              padding: '1rem',
              background: 'var(--bg)',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <div style={{ fontSize: '0.9rem', color: 'var(--text)' }}>
                <strong>Reference:</strong> MK{Date.now().toString().slice(-6)}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid var(--border)',
        padding: '1rem 1.5rem',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '1200px', margin: '0 auto' }}>
          <button
            onClick={() => navigate('/customer')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1rem',
              color: 'var(--text)'
            }}
          >
            <ArrowLeft size={20} />
            <span style={{ fontWeight: 600 }}>Request Service</span>
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Shield size={20} style={{ color: 'var(--green)' }} />
            <span style={{ fontSize: '0.9rem', color: 'var(--muted)' }}>Insurance Active</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem 1rem' }}>
        {/* Progress Steps */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {(() => {
              // Dynamic steps based on service type
              if (serviceType === 'towing') {
                return ['Service', 'Location', 'Service Type', 'Tower', 'Confirm']
              } else if (serviceType === 'fuel') {
                return ['Service', 'Location', 'Tower', 'Confirm']
              } else {
                return ['Service', 'Location', 'Confirm']
              }
            })().map((stepName, index) => {
              const towingSteps = ['service', 'location', 'service_types', 'towers', 'confirm']
              const fuelSteps = ['service', 'location', 'towers', 'confirm']
              const otherSteps = ['service', 'location', 'confirm']
              const stepValues = serviceType === 'towing' ? towingSteps : serviceType === 'fuel' ? fuelSteps : otherSteps
              const currentStepIndex = stepValues.indexOf(step)
              const isActive = step === stepValues[index]
              const isCompleted = currentStepIndex > index

              return (
                <div key={stepName} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: isCompleted ? 'var(--green)' : isActive ? 'var(--red)' : 'var(--border)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}>
                    {isCompleted ? '✓' : index + 1}
                  </div>
                  <span style={{
                    fontSize: '0.75rem',
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? 'var(--text)' : 'var(--muted)'
                  }}>
                    {stepName}
                  </span>
                  {index < 3 && (
                    <div style={{
                      width: '16px',
                      height: '2px',
                      background: isCompleted ? 'var(--green)' : 'var(--border)'
                    }} />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {error && (
          <div className="login-error" style={{ marginBottom: '1.5rem' }}>
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        {/* Step 1: Service Selection */}
        {step === 'service' && (
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>
                Select Service Type
              </h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
              <button
                onClick={() => { setServiceType('towing'); setStep('location'); }}
                style={{
                  padding: '1rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#dc2626'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
              >
                <Truck size={32} style={{ color: '#dc2626' }} />
                <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1f2937' }}>Towing</div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', textAlign: 'center' }}>
                  Vehicle breakdown or needs transport
                </div>
              </button>

              <button
                onClick={() => { setServiceType('battery'); setStep('location'); }}
                style={{
                  padding: '1rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#dc2626'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
              >
                <Battery size={32} style={{ color: '#dc2626' }} />
                <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1f2937' }}>Battery</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', textAlign: 'center' }}>
                  Jump start - KES 1,500
                </div>
              </button>

              <button
                onClick={() => { setServiceType('flat_tyre'); setStep('location'); }}
                style={{
                  padding: '1rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#dc2626'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
              >
                <Wrench size={32} style={{ color: '#dc2626' }} />
                <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1f2937' }}>Flat Tyre</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', textAlign: 'center' }}>
                  Tyre change - KES 800
                </div>
              </button>

              <button
                onClick={() => { setServiceType('fuel'); setStep('location'); }}
                style={{
                  padding: '1rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#dc2626'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
              >
                <Fuel size={32} style={{ color: '#dc2626' }} />
                <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1f2937' }}>Fuel Delivery</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', textAlign: 'center' }}>
                  Emergency fuel delivery
                </div>
              </button>

            </div>
          </div>
        )}

        {/* Step 2: Location */}
        {step === 'location' && (
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <button
                onClick={handleBack}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  color: '#374151',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <ArrowLeft size={16} />
                Back
              </button>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1f2937' }}>
                Your Location
              </h2>
            </div>

            <button
              onClick={detectLocation}
              disabled={gpsStatus === 'loading'}
              style={{
                width: '100%',
                padding: '1rem',
                borderRadius: '12px',
                background: gpsStatus === 'loading' ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: gpsStatus === 'loading' ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                marginBottom: '1rem'
              }}
            >
              {gpsStatus === 'loading' ? <Loader2 size={20} className="spin" /> : <Navigation size={20} />}
              {gpsStatus === 'loading' ? 'Detecting...' : 'Use GPS Location'}
            </button>

            {gpsStatus === 'denied' && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.4rem',
                  padding: '0.75rem', borderRadius: '8px',
                  background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
                  marginBottom: '0.75rem'
                }}>
                  <AlertCircle size={16} style={{ color: '#dc2626', marginTop: '1px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.85rem', color: '#dc2626', lineHeight: 1.4 }}>
                    {gpsError || 'GPS access denied. Please enter location manually.'}
                  </span>
                </div>

                {/* Location permission help if denied */}
                {permissionState === 'denied' && (
                  <div style={{
                    padding: '0.75rem',
                    background: 'rgba(59,130,246,0.05)',
                    borderRadius: '8px',
                    border: '1px solid rgba(59,130,246,0.15)'
                  }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#3b82f6', marginBottom: '0.5rem' }}>
                      How to enable location access:
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#374151', lineHeight: 1.5 }}>
                      <div style={{ marginBottom: '0.4rem' }}>
                        <strong>Chrome/Edge:</strong> Click the lock icon 🔒 in the address bar → Location → Allow
                      </div>
                      <div style={{ marginBottom: '0.4rem' }}>
                        <strong>Firefox:</strong> Click the lock icon 🔒 in the address bar → Permissions → Location → Allow
                      </div>
                      <div style={{ marginBottom: '0.4rem' }}>
                        <strong>Safari:</strong> Safari → Settings for This Website → Location → Allow
                      </div>
                      <div>
                        <strong>After enabling:</strong> Refresh this page and click "Use GPS Location" again
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>or</span>
            </div>

            <input
              type="text"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="Enter location manually..."
              style={{
                width: '100%',
                padding: '1rem',
                borderRadius: '12px',
                border: '2px solid #e5e7eb',
                fontSize: '1rem',
                marginBottom: '1.5rem'
              }}
            />

            {(autoLocationLabel || destination) && (
              <div style={{
                padding: '1rem',
                background: '#f0fdf4',
                border: '2px solid #22c55e',
                borderRadius: '12px',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#16a34a', fontWeight: 600 }}>
                  <CheckCircle2 size={20} />
                  Location detected
                </div>
                <div style={{ color: '#16a34a', marginTop: '0.5rem' }}>
                  {autoLocationLabel || destination}
                </div>
              </div>
            )}

            <button
              onClick={async () => {
                if (serviceType === 'towing') {
                  setStep('service_types')
                } else if (serviceType === 'fuel' || serviceType === 'battery' || serviceType === 'flat_tyre') {
                  // Load matching services for this service type
                  await loadMatchingServices(serviceType)
                  setStep('service_types')
                } else {
                  setStep('confirm')
                }
              }}
              disabled={!gpsLat || !gpsLng}
              style={{
                width: '100%',
                padding: '1rem',
                borderRadius: '12px',
                background: (!gpsLat || !gpsLng) ? '#9ca3af' : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                color: 'white',
                border: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: (!gpsLat || !gpsLng) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {!gpsLat || !gpsLng ? 'Location Required' : (serviceType === 'towing' ? 'Select Service Type' : 'Select Service Type')}
            </button>
          </div>
        )}

        {/* Step 2.5: Fuel Details (only for fuel delivery, shown after location is detected) */}
        {step === 'location' && serviceType === 'fuel' && gpsLat && gpsLng && (
          <div style={{ maxWidth: '500px', margin: '0 auto', marginTop: '1.5rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>
                Fuel Type
              </label>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                {[
                  { id: 'petrol', label: 'Petrol' },
                  { id: 'diesel', label: 'Diesel' },
                  { id: 'kerosene', label: 'Kerosene' }
                ].map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setFuelType(type.id as any)}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: fuelType === type.id ? '2px solid #dc2626' : '1px solid #e5e7eb',
                      background: fuelType === type.id ? '#fef2f2' : 'white',
                      color: fuelType === type.id ? '#dc2626' : '#374151',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>
                Quantity (Liters)
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <button
                  onClick={() => setFuelQuantity(Math.max(1, fuelQuantity - 5))}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    background: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  -
                </button>
                <input
                  type="number"
                  value={fuelQuantity}
                  onChange={(e) => setFuelQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1"
                  step="1"
                  style={{
                    flex: 1,
                    padding: '0.4rem',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    textAlign: 'center'
                  }}
                />
                <button
                  onClick={() => setFuelQuantity(fuelQuantity + 5)}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    background: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  +
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Vehicle Selection (optional - only for towing/fuel) */}
        {step === 'vehicle' && (serviceType === 'towing' || serviceType === 'fuel') && (
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <button
                onClick={handleBack}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  color: '#374151',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <ArrowLeft size={16} />
                Back
              </button>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1f2937' }}>
                Select Vehicle
              </h2>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              {vehicles.length > 0 ? (
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {vehicles.map((vehicle) => (
                    <button
                      key={vehicle.id}
                      onClick={() => setSelectedVehicle(vehicle.id)}
                      style={{
                        width: '100%',
                        padding: '1rem',
                        borderRadius: '12px',
                        border: selectedVehicle === vehicle.id ? '2px solid #dc2626' : '1px solid #e5e7eb',
                        background: selectedVehicle === vehicle.id ? '#fef2f2' : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem'
                      }}
                    >
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem'
                      }}>
                        🚗
                      </div>
                      <div style={{ textAlign: 'left', flex: 1 }}>
                        <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                          {vehicle.plate}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {vehicle.make} {vehicle.model} ({vehicle.year})
                        </div>
                      </div>
                      {selectedVehicle === vehicle.id && (
                        <CheckCircle2 size={20} style={{ color: '#dc2626' }} />
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '2rem',
                  background: '#f9fafb',
                  borderRadius: '12px',
                  border: '1px dashed #e5e7eb'
                }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                    No vehicles registered. Add your vehicle to continue.
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setStep('confirm')}
              style={{
                width: '100%',
                padding: '1rem',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                color: 'white',
                border: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              Continue to Confirmation
            </button>
          </div>
        )}

        {/* Step 3: Select Service Type (for towing, fuel, battery, and flat tyre) */}
        {step === 'service_types' && (
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <button
                onClick={handleBack}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  color: '#374151',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <ArrowLeft size={16} />
                Back
              </button>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1f2937' }}>
                {serviceType === 'towing' ? 'Select Towing Service' : (serviceType === 'fuel' ? 'Select Fuel Delivery Service' : (serviceType === 'battery' ? 'Select Battery Service' : 'Select Flat Tyre Service'))}
              </h2>
            </div>

            {/* Search bar for services */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ position: 'relative' }}>
                <Search size={20} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input
                  type="text"
                  value={serviceSearch}
                  onChange={(e) => setServiceSearch(e.target.value)}
                  placeholder={serviceType === 'towing' ? 'Search services by name or company...' : 'Search services...'}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem 0.875rem 3rem',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    fontSize: '0.95rem',
                    outline: 'none',
                    background: '#f9fafb',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#dc2626'
                    e.currentTarget.style.background = 'white'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb'
                    e.currentTarget.style.background = '#f9fafb'
                  }}
                />
                {serviceSearch && (
                  <button
                    onClick={() => setServiceSearch('')}
                    style={{
                      position: 'absolute',
                      right: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: '#e5e7eb',
                      border: 'none',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: '#6b7280',
                      fontSize: '0.875rem',
                      fontWeight: 600
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {loadingAllServices ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <Loader2 size={48} className="spin" style={{ color: '#3b82f6', margin: '0 auto 1rem' }} />
                <div style={{ color: '#6b7280' }}>Loading available services...</div>
              </div>
            ) : allTowingServices.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <Wrench size={48} style={{ color: '#9ca3af', margin: '0 auto 1rem' }} />
                <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1f2937', marginBottom: '0.5rem' }}>
                  No Services Available
                </div>
                <p style={{ color: '#6b7280' }}>
                  No towers have configured {serviceType === 'fuel' ? 'fuel delivery' : (serviceType === 'battery' ? 'battery' : (serviceType === 'flat_tyre' ? 'flat tyre' : 'towing'))} services yet.
                </p>
              </div>
            ) : (
              <div className="services-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                {allTowingServices
                  .filter(service => {
                    if (!serviceSearch) return true
                    const searchLower = serviceSearch.toLowerCase()
                    return (
                      service.service_name.toLowerCase().includes(searchLower) ||
                      (service as any).profiles?.business_name?.toLowerCase().includes(searchLower) ||
                      (service as any).profiles?.full_name?.toLowerCase().includes(searchLower)
                    )
                  })
                  .map((service) => (
                  <div
                    key={service.id}
                    onClick={() => {
                      console.log('Selected service:', service)
                      console.log('Service ID:', service.id)
                      console.log('Tower ID:', service.tower_id)
                      setSelectedTowerService(service)
                      // Load towers
                      if (gpsLat && gpsLng) {
                        setLoadingTowers(true)
                        loadNearbyTowers(gpsLat, gpsLng, 'towing').then(() => {
                          setLoadingTowers(false)
                          setStep('towers')
                        })
                      } else {
                        setStep('towers')
                      }
                    }}
                    style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '1rem',
                      border: selectedTowerService?.id === service.id ? '2px solid #dc2626' : '1px solid #e5e7eb',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: selectedTowerService?.id === service.id ? '0 4px 12px rgba(220, 38, 38, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedTowerService?.id !== service.id) {
                        e.currentTarget.style.borderColor = '#dc2626'
                        e.currentTarget.style.transform = 'translateY(-2px)'
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.15)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedTowerService?.id !== service.id) {
                        e.currentTarget.style.borderColor = '#e5e7eb'
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'
                      }
                    }}
                  >
                    <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: '0 2px 6px rgba(220, 38, 38, 0.2)'
                      }}>
                        <Truck size={24} color="white" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1f2937', marginBottom: '0.2rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {service.service_name}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.35rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {(service as any).profiles?.business_name || (service as any).profiles?.full_name || 'Unknown Tower'}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: '1.3', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {service.description}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                      {service.requires_destination && (
                        <span style={{
                          background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                          color: '#1e40af',
                          padding: '0.2rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          boxShadow: '0 1px 2px rgba(59, 130, 246, 0.2)'
                        }}>
                          Requires Destination
                        </span>
                      )}
                      {service.requires_vehicle && (
                        <span style={{
                          background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                          color: '#059669',
                          padding: '0.2rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          boxShadow: '0 1px 2px rgba(5, 150, 105, 0.2)'
                        }}>
                          Requires Vehicle
                        </span>
                      )}
                    </div>
                    <div style={{
                      padding: '0.625rem',
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                      borderRadius: '8px',
                      border: '1px solid #fcd34d',
                      boxShadow: '0 1px 4px rgba(251, 191, 36, 0.2)'
                    }}>
                      <div style={{ fontSize: '0.65rem', color: '#92400e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.15rem' }}>
                        Base Price
                      </div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#78350f', marginBottom: '0.15rem' }}>
                        KES {service.base_price.toLocaleString()}
                      </div>
                      {service.per_km_rate > 0 && (
                        <div style={{ fontSize: '0.7rem', color: '#92400e', fontWeight: 500 }}>
                          + KES {service.per_km_rate.toLocaleString()}/km
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 4: Select Tower (for towing, fuel, battery, and flat tyre) */}
        {step === 'towers' && (serviceType === 'towing' || serviceType === 'fuel' || serviceType === 'battery' || serviceType === 'flat_tyre') && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <button
                onClick={handleBack}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  color: '#374151',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <ArrowLeft size={16} />
                Back
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1f2937', margin: 0 }}>
                  {selectedTowerService ? `Towers Offering ${selectedTowerService.service_name}` : (serviceType === 'towing' ? 'Towers Offering Service' : (serviceType === 'fuel' ? 'Fuel Delivery Towers' : (serviceType === 'battery' ? 'Battery Jump Start Towers' : 'Flat Tyre Change Towers')))}
                </h2>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => setSortBy('price')}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: sortBy === 'price' ? '#dc2626' : 'white',
                      color: sortBy === 'price' ? 'white' : '#374151',
                      border: sortBy === 'price' ? 'none' : '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    Price
                  </button>
                  <button
                    onClick={() => setSortBy('distance')}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: sortBy === 'distance' ? '#dc2626' : 'white',
                      color: sortBy === 'distance' ? 'white' : '#374151',
                      border: sortBy === 'distance' ? 'none' : '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    Distance
                  </button>
                  <button
                    onClick={() => setSortBy('eta')}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: sortBy === 'eta' ? '#dc2626' : 'white',
                      color: sortBy === 'eta' ? 'white' : '#374151',
                      border: sortBy === 'eta' ? 'none' : '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    ETA
                  </button>
                </div>
              </div>
            </div>

            {loadingTowers ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <Loader2 size={48} className="spin" style={{ color: '#3b82f6', margin: '0 auto 1rem' }} />
                <div style={{ color: '#6b7280' }}>Finding nearby towers...</div>
              </div>
            ) : towersToShow.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '3rem',
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <Truck size={48} style={{ color: '#9ca3af', margin: '0 auto 1rem' }} />
                <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1f2937', marginBottom: '0.5rem' }}>
                  No towers available
                </div>
                <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                  {selectedTowerService ? 'This tower is not available in your area at the moment.' : 'There are no towers available in your area at the moment.'}
                </p>
                <button
                  onClick={() => setStep('location')}
                  style={{
                    padding: '0.75rem 2rem',
                    borderRadius: '8px',
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Try Different Location
                </button>
              </div>
            ) : (
              <div className="tower-jobs-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: '0.5rem'
              }}>
                {towersToShow.map((tower, index) => {
                  // Always use selected service's rates if available
                  console.log('selectedTowerService:', selectedTowerService)
                  console.log('base_price:', selectedTowerService?.base_price)
                  console.log('per_km_rate:', selectedTowerService?.per_km_rate)
                  const baseRate = selectedTowerService?.base_price || (serviceType === 'towing' ? (tower.base_towing_rate || 1500) : (serviceType === 'fuel' ? (tower.fuel_delivery_base_rate || 500) : (serviceType === 'battery' ? 1500 : (serviceType === 'flat_tyre' ? 800 : 500))))
                  const perKmRate = selectedTowerService?.per_km_rate || (serviceType === 'towing' ? (tower.per_km_rate || 100) : (serviceType === 'fuel' ? (tower.fuel_delivery_per_km_rate || 50) : 50))
                  const distanceCost = tower.distance_km * perKmRate
                  const estimatedCost = baseRate + distanceCost
                  const priceTier = getPriceTier(estimatedCost, allCosts)
                  return (
                  <div
                    key={tower.id}
                    onClick={() => setSelectedTower(tower.id)}
                    style={{
                      background: 'white',
                      borderRadius: '8px',
                      padding: '0.5rem',
                      border: selectedTower === tower.id ? '2px solid #dc2626' : '1px solid #e5e7eb',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: selectedTower === tower.id ? '0 2px 8px rgba(220, 38, 38, 0.15)' : '0 1px 2px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedTower !== tower.id) {
                        e.currentTarget.style.borderColor = '#dc2626'
                        e.currentTarget.style.transform = 'translateY(-2px)'
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.15)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedTower !== tower.id) {
                        e.currentTarget.style.borderColor = '#e5e7eb'
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.1)'
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
                      <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '6px',
                        background: selectedTower === tower.id ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' : 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <Truck size={14} style={{ color: selectedTower === tower.id ? 'white' : '#2563eb' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.75rem', color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {tower.full_name}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
                      <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>
                        {tower.distance_km.toFixed(1)} km
                      </div>
                      <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>
                        ~{tower.eta_minutes} min
                      </div>
                    </div>

                    <div style={{
                      padding: '0.375rem',
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                      borderRadius: '6px',
                      border: '1px solid #fcd34d',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '0.7rem', color: '#92400e', fontWeight: 500, marginBottom: '0.125rem' }}>
                        Base: KES {baseRate.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#92400e', fontWeight: 500, marginBottom: '0.125rem' }}>
                        + {tower.distance_km.toFixed(1)}km × KES {perKmRate.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#78350f' }}>
                        KES {estimatedCost.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '0.55rem', color: '#92400e', fontWeight: 500 }}>
                        {priceTier.label}
                      </div>
                    </div>
                  </div>
                  )
                })}
              </div>
            )}

            <button
              onClick={() => {
                if (!selectedTower) {
                  alert('Please select a tower before continuing. Compare costs and locations to choose the best option.')
                  return
                }
                // Go directly to confirm since service was already selected
                setStep('confirm')
              }}
              disabled={!selectedTower}
              style={{
                width: '100%',
                maxWidth: '400px',
                margin: '2rem auto 0',
                display: 'block',
                padding: '1rem',
                borderRadius: '12px',
                background: !selectedTower ? '#9ca3af' : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                color: 'white',
                border: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: !selectedTower ? 'not-allowed' : 'pointer'
              }}
            >
              {!selectedTower ? 'Select a Tower First' : 'Continue to Confirmation'}
            </button>
          </div>
        )}

        {/* Step 5: Confirmation */}
        {step === 'confirm' && (
          <div style={{ maxWidth: '500px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <button
                onClick={handleBack}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  color: '#374151',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <ArrowLeft size={14} />
                Back
              </button>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
                Confirm Request
              </h2>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '10px',
              padding: '1rem',
              border: '1px solid #e5e7eb'
            }}>
              {/* Vehicle (Optional for all services) */}
            {(selectedVehicle || showNewVehicleForm) && (
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>
                  Vehicle (Optional)
                </label>
                {!showNewVehicleForm ? (
                  <div>
                    {vehicles.length > 0 ? (
                      <select
                        value={selectedVehicle}
                        onChange={e => setSelectedVehicle(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          fontSize: '1rem',
                          outline: 'none',
                          marginBottom: '0.5rem'
                        }}
                      >
                        <option value="">Select a vehicle...</option>
                        {vehicles.map(v => (
                          <option key={v.id} value={v.id}>{v.plate} — {v.make} {v.model}</option>
                        ))}
                      </select>
                    ) : (
                      <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '0.5rem' }}>
                        <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>No vehicles registered</p>
                      </div>
                    )}
                    <button
                      onClick={() => setShowNewVehicleForm(true)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        background: 'white',
                        color: '#dc2626',
                        border: '1px solid #dc2626',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      + Add New Vehicle
                    </button>
                  </div>
                ) : (
                  <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: '0.25rem' }}>
                          Plate *
                        </label>
                        <input
                          type="text"
                          value={newVehicle.plate}
                          onChange={e => setNewVehicle({ ...newVehicle, plate: e.target.value.toUpperCase() })}
                          placeholder="KDA 123A"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: '0.25rem' }}>
                          Make *
                        </label>
                        <input
                          type="text"
                          value={newVehicle.make}
                          onChange={e => setNewVehicle({ ...newVehicle, make: e.target.value })}
                          placeholder="Toyota"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: '0.25rem' }}>
                          Model *
                        </label>
                        <input
                          type="text"
                          value={newVehicle.model}
                          onChange={e => setNewVehicle({ ...newVehicle, model: e.target.value })}
                          placeholder="Corolla"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: '0.25rem' }}>
                          Color *
                        </label>
                        <input
                          type="text"
                          value={newVehicle.color}
                          onChange={e => setNewVehicle({ ...newVehicle, color: e.target.value })}
                          placeholder="Silver"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#374151', marginBottom: '0.25rem' }}>
                          Year
                        </label>
                        <input
                          type="number"
                          value={newVehicle.year}
                          onChange={e => setNewVehicle({ ...newVehicle, year: e.target.value })}
                          placeholder="2020"
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => {
                          setShowNewVehicleForm(false)
                          setNewVehicle({ plate: '', make: '', model: '', year: '', color: '' })
                        }}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          background: 'white',
                          color: '#374151',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => {
                          if (!newVehicle.plate || !newVehicle.make || !newVehicle.model || !newVehicle.color) {
                            alert('Please fill in all required fields')
                            return
                          }

                          const { data, error } = await supabase.from('vehicles').insert({
                            owner_id: profile!.id,
                            plate: newVehicle.plate.toUpperCase(),
                            make: newVehicle.make,
                            model: newVehicle.model,
                            year: newVehicle.year ? parseInt(newVehicle.year) : null,
                            color: newVehicle.color,
                            insurance_policy_number: null,
                            insurer_name: null,
                            policy_active: true
                          }).select().single()

                          if (error) {
                            alert('Error registering vehicle: ' + error.message)
                            return
                          }

                          setVehicles([...vehicles, data as Vehicle])
                          setSelectedVehicle(data.id)
                          setShowNewVehicleForm(false)
                          setNewVehicle({ plate: '', make: '', model: '', year: '', color: '' })
                        }}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          background: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        Save & Select
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Pickup Location</div>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>{autoLocationLabel || destination}</div>
              </div>

              {serviceType === 'towing' && destinationAddress && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Destination</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>
                    {destinationAddress}
                  </div>
                </div>
              )}

              {serviceType === 'fuel' && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Fuel Details</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', fontSize: '0.875rem' }}>
                    <div>
                      <span style={{ color: '#6b7280' }}>{fuelType}:</span> {fuelQuantity}L
                    </div>
                  </div>
                </div>
              )}

              {selectedTower && (() => {
                const selectedTowerData = nearbyTowers.find(t => t.id === selectedTower)
                if (!selectedTowerData) return null
                // Use selected service's rates if available
                const baseRate = selectedTowerService?.base_price || (serviceType === 'towing' ? (selectedTowerData.base_towing_rate || 1500) : (serviceType === 'fuel' ? (selectedTowerData.fuel_delivery_base_rate || 500) : (serviceType === 'battery' ? 1500 : (serviceType === 'flat_tyre' ? 800 : 500))))
                const perKmRate = selectedTowerService?.per_km_rate || (serviceType === 'towing' ? (selectedTowerData.per_km_rate || 100) : (serviceType === 'fuel' ? (selectedTowerData.fuel_delivery_per_km_rate || 50) : 50))
                const distanceCost = selectedTowerData.distance_km * perKmRate
                const totalCost = baseRate + distanceCost
                const priceTier = getPriceTier(totalCost, allCosts)
                return (
                  <div style={{
                    padding: '1.5rem',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    borderRadius: '12px',
                    marginBottom: '1.5rem',
                    border: '2px solid #3b82f6'
                  }}>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Selected Tower
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1f2937', marginBottom: '1rem' }}>
                      {selectedTowerData.full_name}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Distance</div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
                          {selectedTowerData.distance_km.toFixed(1)} km
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>ETA</div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
                          ~{selectedTowerData.eta_minutes} min
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Price Tier</div>
                        <div style={{
                          padding: '0.25rem 0.5rem',
                          background: priceTier.color,
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          color: 'white',
                          display: 'inline-block'
                        }}>
                          {priceTier.label}
                        </div>
                      </div>
                    </div>
                    <div style={{
                      padding: '1rem',
                      background: 'white',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Cost Breakdown
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '0.5rem' }}>
                        <div>
                          <div style={{ fontSize: '0.875rem', color: '#4b5563' }}>Base Rate</div>
                          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Fixed charge</div>
                        </div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
                          KES {baseRate.toLocaleString()}
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '0.5rem' }}>
                        <div>
                          <div style={{ fontSize: '0.875rem', color: '#4b5563' }}>Distance Charge</div>
                          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{selectedTowerData.distance_km.toFixed(1)}km × KES {perKmRate.toLocaleString()}/km</div>
                        </div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
                          KES {distanceCost.toLocaleString()}
                        </div>
                      </div>
                      <div style={{ height: '1px', background: '#e5e7eb', margin: '0.75rem 0' }}></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1f2937' }}>Total Cost</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#dc2626' }}>
                          KES {totalCost.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })()}

              <button
                onClick={handleBookTower}
                disabled={processing}
                style={{
                  width: '100%',
                  padding: '1rem',
                  borderRadius: '12px',
                  background: processing ? '#9ca3af' : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  color: 'white',
                  border: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: processing ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {processing ? <Loader2 size={20} className="spin" /> : <CheckCircle2 size={20} />}
                {processing ? 'Processing...' : 'Confirm & Book Tower'}
              </button>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '10px',
              padding: '1.25rem',
              maxWidth: '350px',
              width: '100%',
              margin: '0 auto'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: '#fef2f2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 0.75rem'
                }}>
                  <AlertCircle size={24} style={{ color: '#dc2626' }} />
                </div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937', marginBottom: '0.4rem' }}>
                  Confirm Request
                </h3>
                <p style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                  Are you sure you want to submit this {serviceType.replace('_', ' ')} request?
                </p>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  style={{
                    flex: 1,
                    padding: '0.6rem',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb',
                    background: 'white',
                    color: '#374151',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSubmit}
                  style={{
                    flex: 1,
                    padding: '0.6rem',
                    borderRadius: '6px',
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Yes, Submit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}